import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { SUPPORTED_LANGUAGES, LANGUAGE_SELF_NAMES } from '../lib/languages';
import { matchSupportedLanguage, LANGUAGE_LOCALE_CODES } from '../lib/localeMatch';
import { track } from '../lib/analytics';
import Select from './ui/Select';

const PROFICIENCY_LEVELS = [1, 2, 3, 4, 5];

function LanguageSetupPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading, getToken, refreshUser } = useAuth();
  const [nativeLanguage, setNativeLanguage] = useState(() => matchSupportedLanguage(navigator.language));
  const [studyLanguage, setStudyLanguage] = useState('Mandarin');
  const [proficiency, setProficiency] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await axios.put(
        `${getApiBaseUrl()}/api/auth/account`,
        {
          nativeLanguage,
          studyLanguage,
          studyLanguageProficiency: proficiency,
          preferredView: 'manage',
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      track('language_set', { studyLanguage, proficiency });
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      track('language_set_failed');
      setError(t('languageSetup.error'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-6 sm:p-8 border border-border">
        <div className="text-center mb-8">
          <div className="text-4xl mb-4">🌍</div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('languageSetup.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('languageSetup.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="nativeLanguage" className="block text-sm font-medium text-foreground mb-2">
              {t('languageSetup.iSpeak')}
            </label>
            <Select
              id="nativeLanguage"
              value={nativeLanguage}
              onChange={(e) => {
                const lang = e.target.value;
                setNativeLanguage(lang);
                const code = LANGUAGE_LOCALE_CODES[lang];
                if (code) i18n.changeLanguage(code);
              }}
              disabled={loading}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>{LANGUAGE_SELF_NAMES[lang]}</option>
              ))}
            </Select>
          </div>

          <div>
            <label htmlFor="studyLanguage" className="block text-sm font-medium text-foreground mb-2">
              {t('languageSetup.iWantToLearn')}
            </label>
            <Select
              id="studyLanguage"
              value={studyLanguage}
              onChange={(e) => setStudyLanguage(e.target.value)}
              disabled={loading}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>{t(`languages.${lang}`)}</option>
              ))}
            </Select>
          </div>

          <div>
            <label htmlFor="proficiency" className="block text-sm font-medium text-foreground mb-2">
              {t('languageSetup.myLevel')}
            </label>
            <Select
              id="proficiency"
              value={proficiency}
              onChange={(e) => setProficiency(Number(e.target.value))}
              disabled={loading}
            >
              {PROFICIENCY_LEVELS.map((level) => (
                <option key={level} value={level}>{level} — {t(`proficiency.${level}`)}</option>
              ))}
            </Select>
          </div>

          {error && (
            <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('languageSetup.saving') : t('languageSetup.getStarted')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LanguageSetupPage;
